import type { FetchApi } from '@backstage/core-plugin-api';
import type {
  CheckpointPull,
  Gate1Draft,
  JourneyStatus,
  Profile,
  ProfileProperty,
  RequirementsV2,
  RolesFile,
  Stage,
} from '@vivicta/asdp-contract';
import { githubApiError } from '@vivicta/github-config';

/**
 * The `/asdp` routes of the application-portal backend (journey spec §8).
 * Every call sends the user's GitHub token: the backend opens checkpoint PRs
 * and searches review requests as the user, never as the App.
 */

export const DRAFT_KINDS = ['gate1', 'requirements', 'production'] as const;
export type DraftKind = (typeof DRAFT_KINDS)[number];

/** Production settings (journey spec §3.6): stored, never provisioned before Gate 4. */
export type ProductionSettings = Record<string, string | boolean>;

export type DraftDocument = {
  gate1: Gate1Draft;
  requirements: RequirementsV2 & {
    /** Every id ever issued, so a deleted one is never reused. */
    issued_ids?: string[];
  };
  production: ProductionSettings;
};

export type FieldMeta = Record<string, { edited_by: string; edited_at: string }>;

export type Draft<K extends DraftKind = DraftKind> = {
  repo: string;
  kind: K;
  doc: DraftDocument[K];
  version: number;
  fieldMeta: FieldMeta;
  /** The checkpoint PR the draft is frozen by, while a decision is pending. */
  frozenByPr?: number;
  updatedAt: string;
};

/** One field-level change; `path` is dot-separated, e.g. "outcome.purpose". */
export type FieldChange = { path: string; value: unknown };

export type PatchResult<K extends DraftKind = DraftKind> = {
  draft: Draft<K>;
  /**
   * Fields someone else changed after `baseVersion` that this patch replaced
   * (last writer wins), with the text that was lost, for the toast.
   */
  overwritten: Array<{ path: string; theirs: unknown; by: string }>;
};

export type Journey = {
  repo: string;
  status: JourneyStatus;
  pulls: CheckpointPull[];
  profile: ProfileProperty;
  stage?: Stage;
  /** A pending profile-lower PR, shown as "P2 → P1 pending". */
  pendingProfile?: { to: Profile; pull: number };
  roles?: RolesFile;
  drafts: Partial<
    Record<DraftKind, { version: number; frozenByPr?: number; updatedAt: string; missing: number }>
  >;
};

export type InboxItem =
  | {
      type: 'decision';
      repo: string;
      pull: number;
      checkpoint: CheckpointPull['checkpoint'];
      title: string;
      html_url: string;
      waitingSince: string;
    }
  | {
      type: 'task';
      repo: string;
      task: 'gate1' | 'requirements' | 'production' | 'changes-requested';
      title: string;
      waitingSince: string;
    };

export type SubmitResult = { pull: number; html_url: string };

export type ProfileChangeResult =
  | { applied: true; profile: Profile; issue: string }
  | { applied: false; pull: number; html_url: string };

export class AsdpApi {
  constructor(
    private readonly baseUrl: string,
    private readonly fetchApi: FetchApi,
    private readonly githubToken: () => Promise<string>,
  ) {}

  private async call<T>(path: string, init: RequestInit = {}): Promise<T> {
    const response = await this.fetchApi.fetch(`${this.baseUrl}/asdp${path}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        'X-GitHub-Token': await this.githubToken(),
        ...init.headers,
      },
    });
    if (!response.ok) {
      throw githubApiError(response.status, await response.text(), 'The portal refused the request');
    }
    return (response.status === 204 ? undefined : await response.json()) as T;
  }

  private static app(repo: string) {
    return `/apps/${encodeURIComponent(repo)}`;
  }

  getJourney(repo: string) {
    return this.call<Journey>(`${AsdpApi.app(repo)}/journey`);
  }

  getDraft<K extends DraftKind>(repo: string, kind: K) {
    return this.call<Draft<K>>(`${AsdpApi.app(repo)}/drafts/${kind}`);
  }

  patchDraft<K extends DraftKind>(
    repo: string,
    kind: K,
    baseVersion: number,
    changes: FieldChange[],
  ) {
    return this.call<PatchResult<K>>(`${AsdpApi.app(repo)}/drafts/${kind}`, {
      method: 'PATCH',
      body: JSON.stringify({ baseVersion, changes }),
    });
  }

  submitDraft(repo: string, kind: 'gate1' | 'requirements') {
    return this.call<SubmitResult>(`${AsdpApi.app(repo)}/drafts/${kind}/submit`, {
      method: 'POST',
    });
  }

  withdrawDraft(repo: string, kind: 'gate1' | 'requirements') {
    return this.call<void>(`${AsdpApi.app(repo)}/drafts/${kind}/withdraw`, {
      method: 'POST',
    });
  }

  getInbox() {
    return this.call<{ items: InboxItem[] }>('/inbox');
  }

  escalate(repo: string, regime: string, note: string) {
    return this.call<{ html_url: string }>(`${AsdpApi.app(repo)}/escalations`, {
      method: 'POST',
      body: JSON.stringify({ regime, note }),
    });
  }

  changeProfile(repo: string, to: Profile, reason: string) {
    return this.call<ProfileChangeResult>(`${AsdpApi.app(repo)}/profile`, {
      method: 'POST',
      body: JSON.stringify({ to, reason }),
    });
  }
}
